import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const AnimatedCard = ({ item, idx, offset, row, isMobile }) => {
  const [ref, inView] = useInView({
    triggerOnce: true, // ✅ Runs animation only once
    threshold: isMobile ? 0.25 : 0.6,
    rootMargin: isMobile ? "0px 0px -10% 0px" : "0px",
  });

  const getCardVariants = (index, cardRow) => {
    if (isMobile) {
      return {
        hidden: { opacity: 0, y: 60, x: 0 },
        visible: {
          opacity: 1,
          y: 0,
          x: 0,
          transition: { duration: 0.5, ease: "easeOut" },
        },
      };
    }

    // ✅ Laptop/Desktop animations (faster + smooth)
    if (cardRow === 1) {
      if (index === 0)
        return {
          hidden: { opacity: 0, x: -100 },
          visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.6, ease: "easeInOut" },
          },
        };
      else if (index === 1)
        return {
          hidden: { opacity: 0, y: -60 },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeInOut" },
          },
        };
      else if (index === 2)
        return {
          hidden: { opacity: 0, x: 100 },
          visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.6, ease: "easeInOut" },
          },
        };
    } else if (cardRow === 2) {
      if (index === 0)
        return {
          hidden: { opacity: 0, x: -100 },
          visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.6, ease: "easeInOut", delay: 0.1 },
          },
        };
      else if (index === 1)
        return {
          hidden: { opacity: 0, y: 60 },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeInOut", delay: 0.1 },
          },
        };
      else if (index === 2)
        return {
          hidden: { opacity: 0, x: 100 },
          visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.6, ease: "easeInOut", delay: 0.1 },
          },
        };
    }

    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.6 } },
    };
  };

  return (
    <div
      className={`col-md-4 col-12 my-3 ${
        idx === 1 && !isMobile ? "mt-3" : "mt-0"
      }`}
      ref={ref}
    >
      <motion.div
        className="WhyWeAreBest-card-border"
        variants={getCardVariants(idx, row)}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <div className="row">
          <div className="col-6">
            <img
              src={item.img}
              alt={`Frame ${item.id}`}
              className="img-fluid"
            />
          </div>
          <div className="col-6 text-end">
            <label className="WhyWeAreBest-card-sideLabel">
              0{idx + 1 + offset}
            </label>
          </div>
        </div>
        <h3 className="WhyWeAreBest-card-h3">{item.heading}</h3>
        <div className="WhyWeAreBest-card-line"></div>
        <ul className="WhyWeAreBest-card-ul">
          {item.containLine.map((line, lineIndex) => (
            <li key={lineIndex} className="WhyWeAreBest-card-li">
              {line}
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
};

export default function WhyWeAreBest() {
  const contain1 = [
    {
      id: 1,
      img: "/images/AboutUs/WhyWeAreBest/Frame1.svg",
      heading: "Client-centric Approach",
      containLine: [
        "The vision behind each client requirement is regarded as the most important aspect.",
        "Considering that, our approach is always focused on transparency and is result-oriented.",
      ],
    },
    {
      id: 2,
      img: "/images/AboutUs/WhyWeAreBest/Frame2.svg",
      heading: "Realistic Timelines",
      containLine: [
        "It is even more essential to stick to deadlines while working in the technology sector.",
        "We constantly consider the timeline and aspire to deliver projects to our clients on time.",
      ],
    },
    {
      id: 3,
      img: "/images/AboutUs/WhyWeAreBest/Frame3.svg",
      heading: "Effective Management",
      containLine: [
        "When any resource from our end is outsourced, it is crucial to have efficient management.",
        "We constantly maintain a single flow of communication with no management load on our clients.",
      ],
    },
  ];

  const contain2 = [
    {
      id: 4,
      img: "/images/AboutUs/WhyWeAreBest/Frame4.svg",
      heading: "Cost effective Approach",
      containLine: [
        "We always take into account serving our clients at budget-friendly prices without sacrificing the quality of outcomes.",
      ],
    },
    {
      id: 5,
      img: "/images/AboutUs/WhyWeAreBest/Frame5.svg",
      heading: "Deep-rooted Relations",
      containLine: [
        "We always believe it is vital to build long-lasting relationships with our clients.",
        "This is directly related to the services we delivered to them test.",
      ],
    },
    {
      id: 6,
      img: "/images/AboutUs/WhyWeAreBest/Frame6.svg",
      heading: "Data Security",
      containLine: [
        "As part of the service-based sector, ensuring client data security is critical.",
        "We always comply with the NDA policy for clients as well as employees.",
      ],
    },
  ];

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="row py-md-5 py-2 mx-3_5-0">
      <div className="col-12 px-3">
        <h2 className="about-heading">Why we are best in Industry!</h2>
      </div>

      <div className="col-12 px-3">
        <div className="row justify-content-center">
          {contain1.map((item, index) => (
            <AnimatedCard
              key={item.id}
              item={item}
              idx={index}
              offset={0}
              row={1}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>

      <div className="col-12 px-3">
        <div className="row justify-content-center">
          {contain2.map((item, index) => (
            <AnimatedCard
              key={item.id}
              item={item}
              idx={index}
              offset={3}
              row={2}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
